import { useEffect, useState, useRef, useCallback } from "react";

import { useAppDispatch, useAppSelector } from "../../../../hooks/redux";
import { setSort } from "../../../../redux/reducers/filter";
import { useSearchRequestsPaginatedQuery } from "../../../../redux/reduxQuery/requestApi";

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortState {
  id: string;
  desc: boolean;
}

export const useGetData = (filterString: string) => {
  const dispatch = useAppDispatch();
  const pageSize = useAppSelector(
    (state) => state.filter?.requestControl?.pageSize
  );
  const searchParam = useAppSelector(
    (state) => state.filter?.requestControl?.searchParam
  );
  const storedSort = useAppSelector(
    (state) => state.filter?.requestControl?.sort
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: Number(pageSize ?? 50),
  });

  const [sorting, setSorting] = useState<SortState[]>(() => {
    if (Array.isArray(storedSort) && storedSort.length > 0) {
      return storedSort as SortState[];
    }
    return [{ id: "submittedDate", desc: false }];
  });

  const [search, setSearch] = useState<string>(
    typeof searchParam === "string" ? searchParam : ""
  );

  // RTK Query hook - handles caching and deduplication automatically
  const { data, isLoading, isFetching, refetch } =
    useSearchRequestsPaginatedQuery({
      filter: filterString,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      sort: sorting[0] || null,
      search: search,
    });

  // Sync sort to Redux for persistence (only when changed)
  const prevSortRef = useRef<string | null>(null);
  useEffect(() => {
    const sortStr = JSON.stringify(sorting);
    if (prevSortRef.current !== sortStr) {
      prevSortRef.current = sortStr;
      dispatch(
        setSort({
          requestControl: {
            sort: sorting && sorting.length > 0 ? sorting : null,
          },
        })
      );
    }
  }, [sorting, dispatch]);

  // Stable callback handlers
  const onPaginationChange = useCallback(
    (
      newPagination:
        | PaginationState
        | ((prev: PaginationState) => PaginationState)
    ) => {
      setPagination(newPagination);
    },
    []
  );

  const onSortingChange = useCallback(
    (newSorting: SortState[] | ((prev: SortState[]) => SortState[])) => {
      setSorting(newSorting);
    },
    []
  );

  const handleSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    // Reset to first page when searching
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    config: {
      data: data?.requests ?? [],
      loading: isLoading || isFetching,
      asyncConfig: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
        pageCount: data ? Math.ceil(data.total / pagination.pageSize) : 0,
        onPaginationChange,
        onSortingChange,
        onSearch: handleSearch,
        sort: sorting,
        facets: { search },
      },
    },
    refetch,
    total: data?.total ?? 0,
  };
};
